"use client";

import { useCallback, useEffect, useState } from "react";
import { useVisitorNewsListSlimQuery } from "./graphql/queries/generated/news.generated";
import { useWidgetInstance } from "./widget-instance-context";

/**
 * localStorage key for the id of the most recent news post the visitor has
 * "seen" (i.e. the latest post at the moment they last opened the News tab).
 * Scoped by publicKey — like `wexio:visitor-session:${publicKey}` — so two
 * integrations embedded on the same origin don't cross-read each other's
 * read state. Falls back to an unscoped key when no publicKey is resolved
 * yet (dev / pre-config).
 */
export function newsLastReadKey(publicKey: string | null): string {
  return publicKey ? `wexio:news-last-read:${publicKey}` : "wexio:news-last-read";
}

/**
 * Pure: is there an unread news post? Unread when a latest id exists AND it
 * differs from the one the visitor last read. A missing stored id counts as
 * unread, so a first-time visitor with existing news sees the indicator.
 */
export function computeNewsUnread(
  latestId: string | null | undefined,
  storedId: string | null,
): boolean {
  if (!latestId) return false;
  return latestId !== storedId;
}

interface UseNewsUnreadOpts {
  /** Gate — only run when the News tab is enabled and we're in real
   *  (non-dummy) mode. When false the hook is inert: no query, never unread. */
  enabled: boolean;
  locale: string;
}

interface NewsUnread {
  /** True when the newest news post hasn't been seen. Drives the launcher +
   *  News-tab dot. Always false while disabled or when there are no posts. */
  hasUnread: boolean;
  /** Mark the current newest post as read (persists its id). Called when the
   *  visitor opens the News tab. No-op until the latest id has loaded. */
  markRead: () => void;
}

/**
 * News read/unread tracking, mirroring `useVisitorUnread` for the messenger.
 * Fetches just the latest post id (slim, limit 1) and compares it to the id
 * persisted in localStorage. Visiting the News tab persists the latest id, so
 * the dot clears until a newer post is published.
 */
export function useNewsUnread({ enabled, locale }: UseNewsUnreadOpts): NewsUnread {
  const instance = useWidgetInstance();
  const publicKey = instance.publicKey;
  const storage = instance.storage;
  const { data } = useVisitorNewsListSlimQuery({
    variables: { args: { locale, limit: 1 } },
    skip: !enabled,
  });
  const latestId = data?.visitorNewsList?.items?.[0]?._id ?? null;

  const [storedId, setStoredId] = useState<string | null>(null);
  useEffect(() => {
    if (typeof window === "undefined") return;
    try {
      setStoredId(storage.getItem(newsLastReadKey(publicKey)));
    } catch {
      setStoredId(null);
    }
  }, [publicKey, storage]);

  const markRead = useCallback(() => {
    if (!latestId) return;
    setStoredId(latestId);
    try {
      storage.setItem(newsLastReadKey(publicKey), latestId);
    } catch {
      // Private mode / blocked storage — keep it in-memory this session.
    }
  }, [latestId, publicKey, storage]);

  return {
    hasUnread: enabled && computeNewsUnread(latestId, storedId),
    markRead,
  };
}
